/**
 * Classification Diagnostics Service
 * 
 * Provides diagnostic tools for investigating classification service issues,
 * particularly rejection errors from the processing optimizer.
 */

import { QuickLoggerFactory } from '@/lib/universal-logger';
import { ProcessingOptimizer, ProcessingMode, RequestComplexityFactors } from './processing-optimizer';
import { PerformanceMonitor } from './performance-monitor';
import { CategoryLoader } from './category-loader';

const logger = QuickLoggerFactory.database();

export interface ClassificationDiagnosticReport {
  timestamp: string;
  systemStatus: {
    circuitBreakerState: string;
    adaptiveThresholds: any;
    modeSuccessRates: any;
    performanceHistorySize: number;
  };
  performanceStats: {
    totalOperations: number;
    successRate: number;
    averageDuration: number;
    p95Duration: number;
    operationsPerSecond: number;
  };
  resourceMetrics: {
    memoryUsageMB: number;
    currentLoad: number;
    queueDepth: number;
  };
  decisionFactors: {
    systemLoad: number;
    averageLatency: number;
    successRate: number;
    queueDepth: number;
    availableCapacity: number;
    requestComplexity: number;
    errorRate: number;
    resourceHealth: number;
  };
  recommendations: string[];
  potentialIssues: string[];
}

export class ClassificationDiagnostics {
  private static instance: ClassificationDiagnostics;
  private readonly optimizer: ProcessingOptimizer;
  private readonly performanceMonitor: PerformanceMonitor;
  private readonly categoryLoader: CategoryLoader;

  private constructor() {
    this.optimizer = ProcessingOptimizer.getInstance();
    this.performanceMonitor = PerformanceMonitor.getInstance();
    this.categoryLoader = CategoryLoader.getInstance();
  }

  public static getInstance(): ClassificationDiagnostics {
    if (!ClassificationDiagnostics.instance) {
      ClassificationDiagnostics.instance = new ClassificationDiagnostics();
    }
    return ClassificationDiagnostics.instance;
  }

  /**
   * Generate a comprehensive diagnostic report
   */
  public async generateDiagnosticReport(samplePrompt: string = "Test prompt for classification"): Promise<ClassificationDiagnosticReport> {
                logger.debug('Generating classification diagnostic report', { operation: 'generateDiagnosticReport' });

     try {
      // Get optimizer status
      const optimizerStatus = this.optimizer.getOptimizerStatus();

      // Get performance stats (last hour)
      const perfStats = this.performanceMonitor.getStats(60 * 60 * 1000);

      // Get resource metrics
      const resourceMetrics = this.performanceMonitor.getResourceMetrics();
      const latestResourceMetrics = resourceMetrics[resourceMetrics.length - 1] || {
        memoryUsageMB: 0,
        cpuUsagePercent: 0,
        activeConnections: 0,
        queueDepth: 0,
        timestamp: Date.now()
      };

      // Simulate a classification request to get decision factors
      const complexityFactors: RequestComplexityFactors = {
        promptLength: samplePrompt.length,
        categoryCount: this.categoryLoader.getCategories().length,
        processingDifficulty: this.estimateProcessingDifficulty(samplePrompt),
        historicalPerformance: 0.8
      };

      const recommendation = await this.optimizer.getProcessingRecommendation(complexityFactors);

      const report: ClassificationDiagnosticReport = {
        timestamp: new Date().toISOString(),
        systemStatus: {
          circuitBreakerState: optimizerStatus.circuitBreakerState,
          adaptiveThresholds: optimizerStatus.adaptiveThresholds,
          modeSuccessRates: Object.fromEntries(optimizerStatus.modeSuccessRates),
          performanceHistorySize: optimizerStatus.performanceHistorySize
        },
        performanceStats: {
          totalOperations: perfStats.totalOperations,
          successRate: perfStats.successRate,
          averageDuration: perfStats.averageDuration,
          p95Duration: perfStats.p95Duration,
          operationsPerSecond: perfStats.operationsPerSecond
        },
        resourceMetrics: {
          memoryUsageMB: latestResourceMetrics.memoryUsageMB,
          currentLoad: latestResourceMetrics.cpuUsagePercent / 100,
          queueDepth: latestResourceMetrics.queueDepth
        },
        decisionFactors: recommendation.factors,
        recommendations: [],
        potentialIssues: []
      };

      // Analyze and add recommendations/issues
      this.analyzeSystemHealth(report);

             logger.debug('Classification diagnostic report generated', {
         operation: 'generateDiagnosticReport',
         recommendedMode: recommendation.mode,
         confidence: recommendation.confidence,
         issueCount: report.potentialIssues.length
       });

      return report;

    } catch (error) {
             logger.error('Failed to generate diagnostic report', {
         operation: 'generateDiagnosticReport',
         error: error instanceof Error ? error.message : String(error)
       });
      throw error;
    }
  }

  /**
   * Test a specific prompt to see why it might be rejected
   */
  public async testPromptClassification(promptText: string): Promise<{
    recommendation: any;
    wouldBeRejected: boolean;
    rejectionReason?: string;
    alternatives: any[];
  }> {
    const complexityFactors: RequestComplexityFactors = {
      promptLength: promptText.length,
      categoryCount: this.categoryLoader.getCategories().length,
      processingDifficulty: this.estimateProcessingDifficulty(promptText),
      historicalPerformance: 0.8
    };

    const recommendation = await this.optimizer.getProcessingRecommendation(complexityFactors);

    return {
      recommendation,
      wouldBeRejected: recommendation.mode === ProcessingMode.REJECTED,
      rejectionReason: recommendation.mode === ProcessingMode.REJECTED ? recommendation.reasoning : undefined,
      alternatives: recommendation.alternatives
    };
  }

  /**
   * Analyze system health and add recommendations/issues to the report
   */
  private analyzeSystemHealth(report: ClassificationDiagnosticReport): void {
    const { decisionFactors, systemStatus, performanceStats } = report;

    // Check for rejection conditions
    if (systemStatus.circuitBreakerState === 'open') {
      report.potentialIssues.push('Circuit breaker is open - system is in protection mode');
      report.recommendations.push('Wait for circuit breaker to reset or investigate underlying issues');
    }

    if (decisionFactors.systemLoad > 0.95) {
      report.potentialIssues.push(`Critical system overload detected (${(decisionFactors.systemLoad * 100).toFixed(1)}%)`);
      report.recommendations.push('Reduce system load or scale up resources');
    }

    if (decisionFactors.resourceHealth < 0.2) {
      report.potentialIssues.push(`Critical resource health (${(decisionFactors.resourceHealth * 100).toFixed(1)}%)`);
      report.recommendations.push('Check memory usage, API health, and system performance');
    }

    if (decisionFactors.errorRate > 0.5) {
      report.potentialIssues.push(`High error rate detected (${(decisionFactors.errorRate * 100).toFixed(1)}%)`);
      report.recommendations.push('Investigate recent errors and API failures');
    }

    // Check for performance issues
    if (performanceStats.successRate < 0.8) {
      report.potentialIssues.push(`Low success rate (${(performanceStats.successRate * 100).toFixed(1)}%)`);
      report.recommendations.push('Review recent failures and error logs');
    }

    if (performanceStats.averageDuration > 2000) {
      report.potentialIssues.push(`High average latency (${performanceStats.averageDuration.toFixed(0)}ms)`);
      report.recommendations.push('Optimize API calls or consider async processing');
    }

    if (decisionFactors.queueDepth > 10) {
      report.potentialIssues.push(`High queue depth (${decisionFactors.queueDepth})`);
      report.recommendations.push('Process async queue or increase processing capacity');
    }

    // Add general recommendations
    if (report.potentialIssues.length === 0) {
      report.recommendations.push('System appears healthy - no immediate issues detected');
    }

    if (performanceStats.totalOperations < 10) {
      report.recommendations.push('Limited performance data available - run more operations for better insights');
    }
  }

  /**
   * Estimate processing difficulty for a prompt
   */
  private estimateProcessingDifficulty(promptText: string): number {
    let difficulty = 0;

    // Length factor
    if (promptText.length > 500) difficulty += 0.2;
    if (promptText.length > 1000) difficulty += 0.2;

    // Complexity factors
    const complexWords = (promptText.match(/\b\w{10,}\b/g) || []).length;
    difficulty += Math.min(complexWords / 20, 0.3);

    // Special characters and formatting
    const specialChars = (promptText.match(/[^\w\s]/g) || []).length;
    difficulty += Math.min(specialChars / 50, 0.2);

    // Multiple sentences
    const sentences = (promptText.match(/[.!?]+/g) || []).length;
    if (sentences > 3) difficulty += 0.1;

    return Math.min(difficulty, 1.0);
  }

  /**
   * Get quick health check for display in UI
   */
  public async getQuickHealthCheck(): Promise<{
    status: 'healthy' | 'degraded' | 'critical';
    issues: string[];
    circuitBreakerOpen: boolean;
    successRate: number;
    avgLatency: number;
  }> {
    const optimizerStatus = this.optimizer.getOptimizerStatus();
    const perfStats = this.performanceMonitor.getStats(5 * 60 * 1000); // Last 5 minutes

    const issues: string[] = [];
    let status: 'healthy' | 'degraded' | 'critical' = 'healthy';

    if (optimizerStatus.circuitBreakerState === 'open') {
      issues.push('Circuit breaker open');
      status = 'critical';
    }

    if (perfStats.successRate < 0.8) {
      issues.push(`Low success rate: ${(perfStats.successRate * 100).toFixed(1)}%`);
      status = status === 'critical' ? 'critical' : 'degraded';
    }

    if (perfStats.averageDuration > 2000) {
      issues.push(`High latency: ${perfStats.averageDuration.toFixed(0)}ms`);
      status = status === 'critical' ? 'critical' : 'degraded';
    }

    return {
      status,
      issues,
      circuitBreakerOpen: optimizerStatus.circuitBreakerState === 'open',
      successRate: perfStats.successRate,
      avgLatency: perfStats.averageDuration
    };
  }

  /**
   * Reset optimizer state (for recovery)
   */
     public resetOptimizer(): void {
     logger.debug('Resetting processing optimizer', { operation: 'resetOptimizer' });
     this.optimizer.reset();
   }

   /**
    * Force circuit breaker to close (use with caution)
    */
   public forceCircuitBreakerClose(): void {
     logger.debug('Forcing circuit breaker to close', { operation: 'forceCircuitBreakerClose' });
     this.optimizer.reset(); // This will reset circuit breaker state
   }

   /**
    * Temporarily disable rejections for debugging (use with extreme caution)
    */
   public temporarilyDisableRejections(): void {
     logger.debug('Temporarily disabling classification rejections', { operation: 'temporarilyDisableRejections' });
     this.optimizer.adjustRejectionThresholds({ temporaryDisable: true });
   }

   /**
    * Restore normal rejection behavior
    */
   public restoreNormalRejections(): void {
     logger.debug('Restoring normal rejection thresholds', { operation: 'restoreNormalRejections' });
     this.optimizer.restoreDefaultRejectionThresholds();
   }
}

export const classificationDiagnostics = ClassificationDiagnostics.getInstance(); 